import { DestroyRef, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { concatMap, filter, finalize } from 'rxjs/operators';

import { DirectoryApiService } from 'src/app/data-transfer/services/directory-api-service';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';
import { SimpleAlertDialogComponent, SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import {
  CreateDataExchangeRequestDialogComponent,
  CreateDataExchangeRequestDialogData,
} from 'src/app/components/directory-tree/dialogs/create-data-exchange-request-dialog/create-data-exchange-request-dialog.component';
import { DialogActions } from 'src/app/model/dictionary';
import { getDialogConfig } from './dialog-util';
import { showApiErrorDialog } from './api-error-util';

export interface DataExchangeRequestTargetIds {
  packagingSystemIds?: number[];
  packagingUnitIds?: number[];
  componentIds?: number[];
  materialIds?: number[];
}

export interface DataExchangeRequestServices {
  dialog: MatDialog;
  directoryApiService: DirectoryApiService;
  dataExchangeApiService: DataExchangeRequestApiService;
  translocoService: TranslocoService;
  destroyRef: DestroyRef;
}

export function openCreateDataExchangeRequestDialog(
  services: DataExchangeRequestServices,
  targetIds: DataExchangeRequestTargetIds,
  isCreatingDataExchangeRequest: WritableSignal<boolean>,
): void {
  if (isCreatingDataExchangeRequest()) {
    return;
  }
  isCreatingDataExchangeRequest.set(true);

  services.directoryApiService.getExternalSecurityPrincipals().pipe(
    finalize(() => isCreatingDataExchangeRequest.set(false)),
    concatMap(securityPrincipals => {
      const dialogData: CreateDataExchangeRequestDialogData = {
        allPrincipals: securityPrincipals,
        ...targetIds,
      };
      return services.dialog.open(CreateDataExchangeRequestDialogComponent, getDialogConfig(dialogData, '50%')).afterClosed();
    }),
    filter(result => !!result && result.event !== DialogActions.REJECT),
    concatMap(result => services.dataExchangeApiService.createDataExchangeRequest(result.dto, true)),
    takeUntilDestroyed(services.destroyRef),
  ).subscribe({
    next: () => {
      const data: SimpleDialogData = {
        title: services.translocoService.translate('common.text.success'),
        messages: [services.translocoService.translate('dataManagement.directory.dialog.dataExchangeRequest.createSuccess')],
        icon: 'info',
      };
      services.dialog.open(SimpleAlertDialogComponent, getDialogConfig(data, 'm'));
    },
    error: error => showApiErrorDialog(
      services.dialog, error, services.translocoService,
      services.translocoService.translate('dataManagement.directory.dialog.dataExchangeRequest.createError')),
  });
}
